import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { ChartMultiSeriesData } from './types';

const DEFAULT_COLORS = ['#10b981', '#f59e0b', '#6366f1', '#ef4444', '#06b6d4', '#8b5cf6'];

interface StackedBarChartProps {
  data: ChartMultiSeriesData;
  colors?: string[];
  /** Renders bars side-by-side per category instead of stacked. Default `false` (stacked). */
  grouped?: boolean;
}

/**
 * Stacked (or grouped) bar chart for multi-series comparison across categories.
 * Fills its parent container via `ResponsiveContainer`.
 *
 * @example
 * <StackedBarChart
 *   data={{
 *     categories: ['Jan', 'Feb'],
 *     series: [{ name: 'Open', values: [10, 14] }, { name: 'Done', values: [30, 22] }],
 *   }}
 *   colors={['#ef4444', '#10b981']}
 * />
 *
 * @example Grouped (side-by-side) bars
 * <StackedBarChart data={data} grouped />
 */
export function StackedBarChart({ data, colors, grouped = false }: StackedBarChartProps) {
  const palette = colors?.length ? colors : DEFAULT_COLORS;

  const chartData = data.categories.map((cat, i) => {
    const row: Record<string, unknown> = { name: cat };
    data.series.forEach((s) => {
      row[s.name] = s.values[i] ?? 0;
    });
    return row;
  });

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.1} />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
        <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={36} />
        <Tooltip
          cursor={{ fill: 'currentColor', fillOpacity: 0.06 }}
          formatter={(value) =>
            typeof value === 'number' ? value.toLocaleString() : String(value ?? '')
          }
        />
        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
        {data.series.map((s, index) => (
          <Bar
            key={s.name}
            dataKey={s.name}
            stackId={grouped ? undefined : 'stack'}
            fill={palette[index % palette.length]}
            radius={grouped || index === data.series.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
          />
        ))}
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}
